import { z } from "zod";
import { DIVIDER_CDT_LEVELS, LOADER_MODES } from "@CommonTypes/backend/PspRules";

export type DividerRuleErrorCode =
  | "DIVIDER_RULE_INVALID"
  | "LOADER_DEPTH_NEGATIVE"
  | "LOADER_DEPTH_NOT_BELOW_SHELF"
  | "LOADER_DEPTH_NOT_BELOW_SHALLOWEST_SHELF"
  /** The frontend's own check; the backend sends the physical depth instead. */
  | "LOADER_DEPTH_MISSING";

export interface DividerRuleError {
  code: DividerRuleErrorCode;
  /** "" for the whole rule. */
  field: string;
  /** For logs and API clients; the frontend words the code and `params`. */
  message: string;
  /** x100, for the two "must be less than" codes. */
  params?: { depth: number };
}

/** Strings are refused, like the other numeric fixture fields. */
const loaderDepthSchema = z.number().finite().min(0).nullable();

/** Loose on what earlier UIs already send (hardware, legacy fields), strict on the loader fields. */
export const dividerPolicySchema = z
  .object({
    source: z.enum(["base", "attribute", "cdt", "items", "none"]),
    attribute: z.string().optional(),
    values: z.array(z.string()).optional(),
    cdtLevel: z.enum(DIVIDER_CDT_LEVELS as unknown as [string, ...string[]]).optional(),
    cdtValues: z.array(z.string()).optional(),
    productCodes: z.array(z.string()).optional(),
    hardware: z.object({}).passthrough().nullable().optional(),
    keepFixtureDividers: z.boolean().optional(),
    loaderProductCodes: z.array(z.string()).optional(),
    loaderDepth: loaderDepthSchema.optional(),
    /** An unknown mode is DIVIDER_RULE_INVALID with `field: "loaderMode"`. */
    loaderMode: z.enum(LOADER_MODES as unknown as [string, ...string[]]).optional(),
  })
  .passthrough();

/** An absent rule is valid: no dividers, no loaders. */
export const validateDividerPolicy = (
  policy: unknown,
  { shallowestShelfDepth }: { shallowestShelfDepth?: number | null } = {},
): DividerRuleError | null => {
  if (policy === undefined || policy === null) return null;
  const parsed = dividerPolicySchema.safeParse(policy);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    const field = String(issue?.path[0] ?? "");
    if (field === "loaderDepth") {
      return {
        code: "LOADER_DEPTH_NEGATIVE",
        field,
        message: "loaderDepth must be a number >= 0 or null",
      };
    }
    return {
      code: "DIVIDER_RULE_INVALID",
      field,
      message: `Invalid divider rule${field ? ` field ${field}` : ""}: ${issue?.message ?? "unreadable"}`,
    };
  }
  const depth = parsed.data.loaderDepth;
  if (
    typeof depth === "number" &&
    depth > 0 &&
    typeof shallowestShelfDepth === "number" &&
    shallowestShelfDepth > 0 &&
    depth >= shallowestShelfDepth
  ) {
    return {
      code: "LOADER_DEPTH_NOT_BELOW_SHALLOWEST_SHELF",
      field: "loaderDepth",
      message: "loaderDepth must be less than the shallowest shelf depth",
      params: { depth: shallowestShelfDepth },
    };
  }
  return null;
};

/** undefined keeps the stored value and null clears it, so both pass. */
export const validateFixtureLoaderDepth = (
  loaderDepth: unknown,
  shelfDepth: number | string | null | undefined,
): DividerRuleError | null => {
  if (loaderDepth === undefined || loaderDepth === null) return null;
  if (!loaderDepthSchema.safeParse(loaderDepth).success) {
    return {
      code: "LOADER_DEPTH_NEGATIVE",
      field: "loaderDepth",
      message: "loaderDepth must be a number >= 0 or null",
    };
  }
  const depth = Number(shelfDepth);
  if (
    (loaderDepth as number) > 0 &&
    Number.isFinite(depth) &&
    depth > 0 &&
    (loaderDepth as number) >= depth
  ) {
    return {
      code: "LOADER_DEPTH_NOT_BELOW_SHELF",
      field: "loaderDepth",
      message: "loaderDepth must be less than the shelf depth",
      params: { depth },
    };
  }
  return null;
};
